<template>
  <div>
    <BasePageContainer class="mandala-goal-select pt-8 pb-4">
      <PageHeader has-back>
        記録一覧
      </PageHeader>

      <div class="mandala-goal-select__section">
        <div class="mandala-goal-select__section-label">
          対象月
        </div>
        <div class="mandala-goal-select__section-content">
          <MonthPickerWithArrow v-model="selectedYearMonthDate" />
        </div>
      </div>

      <div class="mandala-goal-select__section">
        <div class="mandala-goal-select__section-label">
          対象メンバー
        </div>
        <div class="mandala-goal-select__section-content">
          <MemberFilterSection
            :member-filter.sync="memberFilter"
            :selected-department-ids.sync="selectedDepartmentIds"
            :selected-tag1-values.sync="selectedTag1Values"
            :selected-tag2-values.sync="selectedTag2Values"
            :selected-grades.sync="selectedGrades"
            :selected-homeroom-values.sync="selectedHomeroomValues"
            :department-options="departmentOptions" />
        </div>
      </div>

      <ButtonsWrapper ref="buttonsWrapper">
        <template #leftTop>
          <BaseButton
            color="cancel"
            @click="$router.go(-1)">
            キャンセル
          </BaseButton>
        </template>
        <template #rightTop>
          <BaseButton @click="onClickList">
            一覧する
          </BaseButton>
        </template>
      </ButtonsWrapper>
    </BasePageContainer>
    <router-view
      v-if="initialized"
      :key="viewKey" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { format, startOfMonth } from 'date-fns'
import PageHeader from '@/components/parts/PageHeader.vue'
import ButtonsWrapper from '@/components/parts/ButtonsWrapper.vue'
import BaseButton from '@/components/parts/BaseButton.vue'
import MonthPickerWithArrow from '@/components/parts/MonthPickerWithArrow.vue'
import MemberFilterSection from '@/components/view/records/MemberFilterSection.vue'

const STORAGE_KEY = 'recordListMandalaGoalForm'
const FORM_FIELDS = [
  'memberFilter',
  'selectedDepartmentIds',
  'selectedTag1Values',
  'selectedTag2Values',
  'selectedGrades',
  'selectedHomeroomValues',
]

export default {
  components: {
    PageHeader,
    ButtonsWrapper,
    BaseButton,
    MonthPickerWithArrow,
    MemberFilterSection,
  },
  data() {
    return {
      selectedYearMonthDate: startOfMonth(new Date()),
      memberFilter: null,
      selectedDepartmentIds: [],
      selectedTag1Values: [],
      selectedTag2Values: [],
      selectedGrades: [],
      selectedHomeroomValues: [],
      initialized: false,
      viewKey: 0,
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    selectedYearMonthStr() {
      return format(this.selectedYearMonthDate, 'yyyy-MM-dd')
    },
    accessibleDepartments() {
      if (!this.user) return []
      return this.user.accessibleDepartments || []
    },
    departmentOptions() {
      return this.accessibleDepartments.map(d => ({ value: d.id, text: d.name }))
    },
    isAnyMemberSelected() {
      if (!this.memberFilter) return false
      if (this.memberFilter === 'groupTag') {
        return this.selectedDepartmentIds.length > 0
          || this.selectedTag1Values.length > 0
          || this.selectedTag2Values.length > 0
      }
      if (this.memberFilter === 'gradeHomeroom') {
        return this.selectedGrades.length > 0
          || this.selectedHomeroomValues.length > 0
      }
      return false
    },
  },
  async created() {
    this.$store.commit('startLoading')
    try {
      const tenantInfo = await this.$axios.get('/tenants/').then(res => res.data).catch(() => null)
      if (tenantInfo) {
        this.$store.commit('tenant/setTenantInfo', tenantInfo)
      }
      if (Object.keys(this.$route.query).length > 0) {
        this.restoreFormStateFromQuery()
      } else {
        this.restoreFormState()
      }
      this.filterInvalidDepartmentIds()
      if (!this.$store.getters['tenant/studentNumberOption']) {
        this.memberFilter = 'groupTag'
      }
    } finally {
      this.$store.commit('finishLoading')
      this.initialized = true
    }
  },
  methods: {
    filterInvalidDepartmentIds() {
      const validIds = new Set(this.departmentOptions.map(o => o.value))
      this.selectedDepartmentIds = this.selectedDepartmentIds.filter(id => validIds.has(id))
    },
    onClickList() {
      if (!this.isAnyMemberSelected) {
        this.$store.commit('notify/set', { message: '対象メンバーを選択してください', type: 'danger' })
        return
      }
      this.proceedToList()
    },
    async proceedToList() {
      this.saveFormState()
      const query = this.buildQueryParams()
      if (this.$route.name === 'recordListMandalaGoalList') {
        await this.$router.replace({ name: 'recordListMandalaGoalList', query }).catch(() => {})
        this.viewKey++
      } else {
        await this.$router.push({ name: 'recordListMandalaGoalList', query }).catch(() => {})
      }

      await this.$nextTick()
      if (this.$refs.buttonsWrapper) {
        this.$refs.buttonsWrapper.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    buildQueryParams() {
      const q = { yearMonth: this.selectedYearMonthStr }
      if (this.memberFilter === 'groupTag') {
        if (this.selectedDepartmentIds.length) q.departments = this.selectedDepartmentIds.join(',')
        if (this.selectedTag1Values.length) q.tag1 = this.selectedTag1Values.join(',')
        if (this.selectedTag2Values.length) q.tag2 = this.selectedTag2Values.join(',')
      } else if (this.memberFilter === 'gradeHomeroom') {
        if (this.selectedGrades.length) q.grades = this.selectedGrades.join(',')
        if (this.selectedHomeroomValues.length) q.homerooms = this.selectedHomeroomValues.join(',')
      }
      return q
    },
    saveFormState() {
      try {
        const state = { selectedYearMonthStr: this.selectedYearMonthStr }
        FORM_FIELDS.forEach(key => { state[key] = this[key] })
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
      } catch (e) { /* localStorage が使用不可の場合は無視 */ }
    },
    restoreFormState() {
      try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (!saved) return
        const state = JSON.parse(saved)
        if (state.selectedYearMonthStr) {
          this.selectedYearMonthDate = startOfMonth(new Date(state.selectedYearMonthStr))
        }
        FORM_FIELDS.forEach(key => {
          if (key in state) this[key] = state[key]
        })
      } catch (e) { /* localStorage が使用不可の場合は無視 */ }
    },
    restoreFormStateFromQuery() {
      const q = this.$route.query
      if (q.yearMonth) this.selectedYearMonthDate = startOfMonth(new Date(q.yearMonth))
      this.selectedDepartmentIds = q.departments ? q.departments.split(',') : []
      this.selectedTag1Values = q.tag1 ? q.tag1.split(',') : []
      this.selectedTag2Values = q.tag2 ? q.tag2.split(',') : []
      this.selectedGrades = q.grades ? q.grades.split(',').map(Number) : []
      this.selectedHomeroomValues = q.homerooms ? q.homerooms.split(',') : []
      if (q.departments || q.tag1 || q.tag2) {
        this.memberFilter = 'groupTag'
      } else if (q.grades || q.homerooms) {
        this.memberFilter = 'gradeHomeroom'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.mandala-goal-select {
  &__section {
    display: flex;
    margin-bottom: 24px;
    border: 1px solid $grey11;
    padding: 16px;
    border-radius: 4px;
  }

  &__section-label {
    width: 80px;
    flex-shrink: 0;
    font-weight: bold;
    font-size: $fs-s;
    padding: 4px 8px;
    background-color: $grey03;
    border-radius: 4px;
    height: fit-content;
  }

  &__section-content {
    flex: 1;
    padding-left: 16px;
  }

}

@media print {
  .mandala-goal-select {
    display: none;
  }
}
</style>
